import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Clock, MapPin, Handshake } from "lucide-react";
import { toast } from "sonner";
import { sendContact } from "@/lib/api";

const CATEGORIES = ["General", "Order inquiry", "Gut sensitivity question", "Wholesale", "Press"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", category: "General", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in all fields.");
    setLoading(true);
    try {
      const res = await sendContact(form);
      toast.success(res.message || "Message sent!");
      setForm({ name: "", email: "", category: "General", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#080806] pt-[70px]">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <p className="font-mono-clinical text-xs uppercase tracking-[0.25em] text-[#D6A921]">Contact & Support</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
              We're serious about your gut.
            </h1>
            <p className="mt-4 max-w-md text-[#F7F6F0]">
              Questions about sensitivities, orders, or formulations? Our in-house nutrition team
              reads every message.
            </p>

            <div className="mt-10 space-y-4">
              {[
                { icon: Mail, title: "Customer care", desc: "help@lenisnutrition.co.uk", href: "mailto:help@lenisnutrition.co.uk" },
                { icon: Handshake, title: "Partnerships", desc: "partner@lenisnutrition.co.uk", href: "mailto:partner@lenisnutrition.co.uk" },
                { icon: MapPin, title: "Office", desc: "Flat 79 Hatton Place, Luton, LU2 0FD" },
                { icon: Clock, title: "Response time", desc: "Within 24 hours, every day" },
              ].map((c) => (
                <div key={c.title} data-testid={`contact-info-${c.title.toLowerCase().replace(/\s/g, "-")}`} className="flex gap-4 rounded-2xl border border-white/10 bg-[#30302B] p-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#D6A921]/10">
                    <c.icon size={19} className="text-[#D6A921]" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-white">{c.title}</p>
                    {c.href
                      ? <a href={c.href} className="text-sm text-[#D6A921] hover:text-white break-all">{c.desc}</a>
                      : <p className="text-sm text-[#A1A1A6]">{c.desc}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={submit}
            className="rounded-3xl border border-white/10 bg-[#161616] p-6 sm:p-8"
          >
            <div className="grid gap-4">
              <div>
                <label htmlFor="contact-name" className="text-sm font-medium text-[#A1A1A6]">Name</label>
                <input autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" id="contact-name" data-testid="contact-name"
                  className="mt-1.5 w-full rounded-xl border border-white/10 bg-[#080806] px-4 py-3 text-sm text-white placeholder:text-[#A1A1A6] outline-none focus:border-[#D6A921] transition-colors" />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-sm font-medium text-[#A1A1A6]">Email</label>
                <input autoComplete="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" id="contact-email" data-testid="contact-email"
                  className="mt-1.5 w-full rounded-xl border border-white/10 bg-[#080806] px-4 py-3 text-sm text-white placeholder:text-[#A1A1A6] outline-none focus:border-[#D6A921] transition-colors" />
              </div>
              <div>
                <label htmlFor="contact-category" className="text-sm font-medium text-[#A1A1A6]">Category</label>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} id="contact-category" data-testid="contact-category"
                  className="mt-1.5 w-full rounded-xl border border-white/10 bg-[#080806] px-4 py-3 text-sm text-white outline-none focus:border-[#D6A921] transition-colors">
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#080806]">{c}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="text-sm font-medium text-[#A1A1A6]">Message</label>
                <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} placeholder="How can we help?" id="contact-message" data-testid="contact-message"
                  className="mt-1.5 w-full resize-none rounded-xl border border-white/10 bg-[#080806] px-4 py-3 text-sm text-white placeholder:text-[#A1A1A6] outline-none focus:border-[#D6A921] transition-colors" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-form-submit"
                className="mt-2 w-full rounded-full bg-[#D6A921] py-4 text-sm font-bold uppercase tracking-wide text-[#080806] hover:bg-white transition-colors disabled:opacity-60">
                {loading ? "Sending…" : "Send message"}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </main>
  );
}

